import { Link } from "react-router-dom";
import { BookOpen, CalendarCheck, ClipboardCheck, Hourglass, Megaphone, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, StatCard, InactiveNotice, EmptyState } from "@/components/dash/Common";
import { FullLoader } from "@/components/ProtectedRoute";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { daysLeft, fmtDate, fmtDateTime } from "@/lib/format";

function Upcoming({ items }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="upcoming-classes">
      <div className="flex justify-between items-center mb-4"><h2 className="font-display font-semibold">Jadwal terdekat</h2><Link to="/siswa/kelas" className="text-xs text-amber-600 hover:underline">Lihat semua</Link></div>
      {items.length === 0 ? <p className="text-sm text-slate-500">Belum ada jadwal kelas.</p> : (
        <ul className="divide-y divide-slate-100">
          {items.map((c) => (
            <li key={c.id} className="py-3 flex items-center justify-between gap-3">
              <div className="flex gap-3"><Video className="h-4 w-4 mt-1 text-amber-500" /><div><p className="font-medium text-slate-900">{c.title}</p><p className="text-xs text-slate-500">{fmtDateTime(c.start_at)} · {c.duration_min} menit</p></div></div>
              {c.meeting_url && <Button asChild size="sm" variant="outline"><a href={c.meeting_url} target="_blank" rel="noreferrer">Gabung</a></Button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Announcements({ items }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="student-announcements">
      <h2 className="font-display font-semibold mb-4 flex items-center gap-2"><Megaphone className="h-4 w-4 text-amber-500" />Pengumuman</h2>
      {items.length === 0 ? <p className="text-sm text-slate-500">Belum ada pengumuman.</p> : items.map((a) => (
        <div key={a.id} className="py-3 border-b border-slate-100 last:border-0">
          <p className="font-medium text-slate-900">{a.title}</p>
          <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{a.body}</p>
          <p className="text-xs text-slate-400 mt-1">{fmtDate(a.created_at)}</p>
        </div>
      ))}
    </div>
  );
}

export default function StudentOverview() {
  const { user } = useAuth();
  const { data, loading } = useApi("/student/summary");
  if (loading || !data) return <FullLoader />;
  return (
    <div data-testid="student-overview-page">
      <PageHeader eyebrow="Ringkasan Belajar" title={`Halo, ${user.name.split(" ")[0]}`} description={data.package ? `Paket: ${data.package.name}` : "Anda belum memiliki paket aktif."} />
      {!data.package_active && <div className="mb-6"><InactiveNotice /></div>}
      {data.pending_payment && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4 flex flex-wrap justify-between items-center gap-3" data-testid="pending-payment-banner">
          <p className="text-sm text-slate-700">Tagihan <span className="font-mono font-semibold">{data.pending_payment.invoice}</span> menunggu proses.</p>
          <Button asChild size="sm" variant="outline"><Link to={`/pembayaran?invoice=${data.pending_payment.invoice}`}>Lihat Pembayaran</Link></Button>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard dark label="Masa aktif" value={data.package_active ? `${daysLeft(data.package_expires_at)} hari` : "-"} hint={data.package_active ? `s.d. ${fmtDate(data.package_expires_at)}` : "Tidak aktif"} icon={Hourglass} testid="stat-days-left" />
        <StatCard label="Progres materi" value={`${data.progress_pct}%`} hint={`${data.materials_done}/${data.materials_total} materi`} icon={BookOpen} testid="stat-progress" />
        <StatCard label="Rata-rata nilai" value={data.avg_score ?? "-"} hint={`${data.exams_taken} ujian`} icon={ClipboardCheck} testid="stat-avg-score" />
        <StatCard label="Kehadiran" value={data.attendance_pct != null ? `${data.attendance_pct}%` : "-"} hint="dari kelas yang sudah berlangsung" icon={CalendarCheck} testid="stat-attendance" />
      </div>
      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        {data.package_active ? <Upcoming items={data.upcoming} /> : <EmptyState text="Jadwal kelas tampil setelah paket aktif." />}
        <Announcements items={data.announcements} />
      </div>
    </div>
  );
}
